import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, Copy, Check, Send, Star, MessageSquare, Trash2, Lock, User, CheckCircle, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api/kennel";

// Simple credentials - in production use proper auth
const ADMIN_LOGIN = "Martyna";
const ADMIN_PASSWORD = "Martyna";

const KennelAdminReviews = ({ embedded = false }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(embedded);
  const [loginForm, setLoginForm] = useState({ login: "", password: "" });
  const [loginError, setLoginError] = useState("");
  
  const [tokens, setTokens] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerEmail, setNewCustomerEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [responseText, setResponseText] = useState({});
  const [loading, setLoading] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const saved = sessionStorage.getItem("kennel_admin_logged");
    if (saved === "true") {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      setLoading(true);
      loadData();
    }
  }, [isLoggedIn]);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError("");
    
    if (loginForm.login === ADMIN_LOGIN && loginForm.password === ADMIN_PASSWORD) {
      setIsLoggedIn(true);
      sessionStorage.setItem("kennel_admin_logged", "true");
    } else {
      setLoginError("Nieprawidłowy login lub hasło");
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    sessionStorage.removeItem("kennel_admin_logged");
  };

  const loadData = async () => {
    try {
      const [tokensRes, reviewsRes] = await Promise.all([
        fetch(`${API}/admin/review-tokens`),
        fetch(`${API}/admin/reviews`)
      ]);
      setTokens(await tokensRes.json());
      setReviews(await reviewsRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateLink = async () => {
    try {
      const res = await fetch(`${API}/admin/review-tokens`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_name: newCustomerName, customer_email: newCustomerEmail })
      });
      const data = await res.json();
      setTokens(prev => [{ token: data.token, customer_name: newCustomerName, customer_email: newCustomerEmail, used: false, created_at: new Date().toISOString() }, ...prev]);
      setNewCustomerName("");
      setNewCustomerEmail("");
      
      if (data.email_sent) {
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 4000);
      }
      
      // Auto copy
      navigator.clipboard.writeText(data.link);
      setCopiedToken(data.token);
      setTimeout(() => setCopiedToken(null), 3000);
    } catch (err) {
      console.error(err);
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`https://wolfevomkatzenberg.pl/dodaj-opinie/${token}`);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 3000);
  };

  const deleteToken = async (token) => {
    if (!window.confirm("Czy na pewno usunąć ten link?")) return;
    try {
      await fetch(`${API}/admin/review-tokens/${token}`, { method: "DELETE" });
      setTokens(prev => prev.filter(t => t.token !== token));
    } catch (err) {
      console.error(err);
    }
  };

  const submitResponse = async (reviewId) => {
    const text = responseText[reviewId];
    if (!text) return;
    
    try {
      await fetch(`${API}/admin/reviews/${reviewId}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ response: text })
      });
      setReviews(prev => prev.map(r => 
        r.id === reviewId ? { ...r, breeder_response: text, breeder_response_date: new Date().toISOString() } : r
      ));
      setResponseText(prev => ({ ...prev, [reviewId]: "" }));
    } catch (err) {
      console.error(err);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm("Czy na pewno usunąć tę opinię?")) return;
    try {
      await fetch(`${API}/admin/reviews/${reviewId}`, { method: "DELETE" });
      setReviews(prev => prev.filter(r => r.id !== reviewId));
    } catch (err) {
      console.error(err);
    }
  };

  const approveReview = async (reviewId) => {
    try {
      await fetch(`${API}/admin/reviews/${reviewId}/approve`, { method: "PUT" });
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, approved: true } : r));
    } catch (err) {
      console.error(err);
    }
  };

  const rejectReview = async (reviewId) => {
    try {
      await fetch(`${API}/admin/reviews/${reviewId}/reject`, { method: "PUT" });
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, approved: false } : r));
    } catch (err) {
      console.error(err);
    }
  };

  if (loading && isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="text-center mb-8">
            <img src="/logo_katzenberg_new.png" alt="Wolfe vom Katzenberg" className="h-20 mx-auto mb-4 drop-shadow-[0_0_15px_rgba(212,175,55,0.3)]" />
            <h1 className="text-xl font-serif text-[#F5F5F5]">Wolfe vom Katzenberg</h1>
            <p className="text-[#B8B8B8]/60 text-sm">Panel administracyjny</p>
          </div>
          
          <form onSubmit={handleLogin} className="bg-[#141416] border border-[#2A2A2D] p-6 rounded-lg">
            <div className="mb-4">
              <label className="block text-[#B8B8B8] text-sm mb-2 flex items-center gap-2">
                <User className="w-4 h-4 text-[#D4AF37]" />
                Login
              </label>
              <input
                type="text"
                value={loginForm.login}
                onChange={(e) => setLoginForm(prev => ({ ...prev, login: e.target.value }))}
                placeholder="Wpisz login"
                className="w-full bg-[#0B0B0D] border border-[#2A2A2D] px-4 py-3 rounded-lg text-[#F5F5F5] placeholder-[#B8B8B8]/30 focus:border-[#D4AF37]/50 focus:outline-none"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-[#B8B8B8] text-sm mb-2 flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#D4AF37]" />
                Hasło
              </label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Wpisz hasło"
                className="w-full bg-[#0B0B0D] border border-[#2A2A2D] px-4 py-3 rounded-lg text-[#F5F5F5] placeholder-[#B8B8B8]/30 focus:border-[#D4AF37]/50 focus:outline-none"
              />
            </div>
            
            {loginError && (
              <p className="text-red-400 text-sm mb-4 text-center">{loginError}</p>
            )}
            
            <button
              type="submit"
              className="w-full bg-[#D4AF37] text-[#0B0B0D] py-3 font-medium rounded-lg hover:bg-[#C4A030] transition-colors"
            >
              Zaloguj się
            </button>
          </form>
          
          <p className="text-center text-[#B8B8B8]/30 text-xs mt-6">
            Made with love <a href="https://apisdesign.pl" className="hover:text-[#D4AF37]">apisdesign.pl</a>
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "min-h-screen bg-[#0B0B0D] py-8 px-4"}>
      <div className={embedded ? "" : "max-w-4xl mx-auto"}>
        {/* Header */}
        {!embedded && (
        <>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-serif text-[#F5F5F5]">Panel Opinii</h1>
            <p className="text-[#B8B8B8]/60 text-sm">Zarządzaj opiniami klientów</p>
          </div>
          <a href="/" className="text-[#D4AF37] text-sm hover:underline">← Wróć do strony</a>
        </div>
        <div className="flex gap-3 mb-8">
          <a href="/admin/opinie" className="px-4 py-2 bg-[#D4AF37] text-[#0B0B0D] text-xs font-semibold tracking-wider">OPINIE</a>
          <a href="/admin/galeria" className="px-4 py-2 border border-[#2A2A2D] text-[#B8B8B8] text-xs tracking-wider hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">GALERIA</a>
        </div>
        </>
        )}

        {/* Generate Link Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#141416] border border-[#2A2A2D] p-6 mb-8"
        >
          <h2 className="text-[#F5F5F5] font-medium mb-4 flex items-center gap-2">
            <Link className="w-5 h-5 text-[#D4AF37]" />
            Generuj link do opinii
          </h2>
          <div className="flex flex-col gap-3">
            <div className="flex gap-3">
              <input
                type="text"
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                placeholder="Imię klienta (opcjonalnie)"
                className="flex-1 bg-[#0B0B0D] border border-[#2A2A2D] px-4 py-3 text-[#F5F5F5] placeholder-[#B8B8B8]/30 focus:border-[#D4AF37]/50 focus:outline-none"
              />
              <input
                type="email"
                value={newCustomerEmail}
                onChange={(e) => setNewCustomerEmail(e.target.value)}
                placeholder="Email klienta (opcjonalnie — wyśle zaproszenie)"
                className="flex-1 bg-[#0B0B0D] border border-[#2A2A2D] px-4 py-3 text-[#F5F5F5] placeholder-[#B8B8B8]/30 focus:border-[#D4AF37]/50 focus:outline-none"
                data-testid="review-customer-email"
              />
              <button
                onClick={generateLink}
                className="bg-[#D4AF37] text-[#0B0B0D] px-6 py-3 font-medium hover:bg-[#C4A030] transition-colors flex items-center gap-2"
              >
                <Link className="w-4 h-4" />
                Generuj
              </button>
            </div>
            {emailSent && <p className="text-green-400 text-xs">Email z linkiem do opinii został wysłany!</p>}
            <p className="text-[#B8B8B8]/40 text-xs">Link zostanie automatycznie skopiowany do schowka. Jeśli podasz email — klient dostanie zaproszenie mailem.</p>
          </div>
        </motion.div>

        {/* Generated Links */}
        {tokens.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#141416] border border-[#2A2A2D] p-6 mb-8"
          >
            <h2 className="text-[#F5F5F5] font-medium mb-4">Wygenerowane linki</h2>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {tokens.slice(0, 10).map((t) => (
                <div key={t.token} className={`p-3 rounded ${t.used ? 'bg-[#2A2A2D]/30' : 'bg-[#0B0B0D]'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[#F5F5F5] text-sm">
                      {t.customer_name || "Bez imienia"}
                      {t.used && <span className="text-green-500 ml-2 text-xs">✓ użyty</span>}
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => copyLink(t.token)}
                        disabled={t.used}
                        className={`p-2 transition-colors ${t.used ? 'text-[#B8B8B8]/20' : 'text-[#B8B8B8] hover:text-[#D4AF37]'}`}
                      >
                        {copiedToken === t.token ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => deleteToken(t.token)}
                        className="p-2 text-[#B8B8B8]/40 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <input
                    type="text"
                    readOnly
                    value={`https://wolfevomkatzenberg.pl/dodaj-opinie/${t.token}`}
                    onClick={(e) => e.target.select()}
                    className="w-full bg-[#141416] border border-[#2A2A2D] px-3 py-2 text-[#D4AF37] text-xs rounded cursor-text select-all"
                  />
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Reviews Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#141416] border border-[#2A2A2D] p-6"
        >
          <h2 className="text-[#F5F5F5] font-medium mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
            Opinie ({reviews.length})
          </h2>
          
          {reviews.length === 0 ? (
            <p className="text-[#B8B8B8]/40 text-sm text-center py-8">Brak opinii</p>
          ) : (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div key={review.id} className={`border-b border-[#2A2A2D] pb-6 last:border-0 ${!review.approved ? 'opacity-60' : ''}`}>
                  {/* Review header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {review.image_url && (
                        <img src={review.image_url} alt="" className="w-12 h-12 rounded object-cover" />
                      )}
                      <div>
                        <p className="text-[#F5F5F5] font-medium">{review.dog_name}</p>
                        <p className="text-[#B8B8B8]/50 text-xs">{review.owner_name} • {review.owner_city}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!review.approved ? (
                        <button onClick={() => approveReview(review.id)}
                          className="text-green-400 text-xs bg-green-500/10 px-3 py-1.5 hover:bg-green-500/20 transition-colors flex items-center gap-1"
                          data-testid={`approve-review-${review.id}`}>
                          <CheckCircle className="w-3.5 h-3.5" /> Zatwierdź
                        </button>
                      ) : (
                        <button onClick={() => rejectReview(review.id)}
                          className="text-yellow-400 text-xs bg-yellow-500/10 px-3 py-1.5 hover:bg-yellow-500/20 transition-colors flex items-center gap-1"
                          data-testid={`reject-review-${review.id}`}>
                          <XCircle className="w-3.5 h-3.5" /> Cofnij
                        </button>
                      )}
                      {!review.approved && (
                        <span className="text-yellow-500 text-xs bg-yellow-500/10 px-2 py-1">Oczekuje</span>
                      )}
                      {review.approved && (
                        <span className="text-green-500 text-xs bg-green-500/10 px-2 py-1">Widoczna</span>
                      )}
                      <button onClick={() => deleteReview(review.id)} className="text-red-400/50 hover:text-red-400 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Stars */}
                  <div className="flex gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#2A2A2D]'}`} />
                    ))}
                  </div>
                  
                  {/* Review text */}
                  <p className="text-[#B8B8B8] text-sm mb-4">{review.text}</p>
                  
                  {/* Breeder response */}
                  {review.breeder_response ? (
                    <div className="bg-[#D4AF37]/5 border-l-2 border-[#D4AF37]/30 p-4 mt-4">
                      <p className="text-[#D4AF37] text-xs font-medium mb-1">Twoja odpowiedź:</p>
                      <p className="text-[#B8B8B8]/80 text-sm italic">„{review.breeder_response}"</p>
                    </div>
                  ) : (
                    <div className="mt-4">
                      <p className="text-[#B8B8B8]/50 text-xs mb-2">Dodaj odpowiedź:</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={responseText[review.id] || ""}
                          onChange={(e) => setResponseText(prev => ({ ...prev, [review.id]: e.target.value }))}
                          placeholder="Napisz osobistą odpowiedź..."
                          className="flex-1 bg-[#0B0B0D] border border-[#2A2A2D] px-3 py-2 text-[#F5F5F5] text-sm placeholder-[#B8B8B8]/30 focus:border-[#D4AF37]/50 focus:outline-none"
                        />
                        <button
                          onClick={() => submitResponse(review.id)}
                          disabled={!responseText[review.id]}
                          className="bg-[#D4AF37] text-[#0B0B0D] px-4 py-2 font-medium hover:bg-[#C4A030] transition-colors disabled:opacity-50"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Footer */}
        <p className="text-center text-[#B8B8B8]/30 text-xs mt-8">
          Made with love <a href="https://apisdesign.pl" className="hover:text-[#D4AF37]">apisdesign.pl</a>
        </p>
      </div>
    </div>
  );
};

export default KennelAdminReviews;
